<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
  AVAILABLE_MENU_PERMISSIONS,
  MENU_PERMISSION_LABELS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

// Get custom menus from store
const customMenus = useMapGetter('customMenu/getCustomMenus');

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);
const selectedMenuPermissions = ref([]);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  selectedMenuPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];

  // Convert menu_permissions object to array of enabled menus
  const menuPerms = props.selectedRole.menu_permissions || {};

  // If menu_permissions is empty, it means "all menus enabled" (backward compatibility)
  // Pre-select all static menus in this case to match backend behavior
  if (Object.keys(menuPerms).length === 0) {
    selectedMenuPermissions.value = [...AVAILABLE_MENU_PERMISSIONS];
  } else {
    selectedMenuPermissions.value = Object.keys(menuPerms).filter(
      key => menuPerms[key] === true
    );
  }
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

// Compute all menu permissions (static + custom menus)
const allMenuPermissions = computed(() => {
  const staticMenus = AVAILABLE_MENU_PERMISSIONS.map(key => ({
    key,
    label: MENU_PERMISSION_LABELS[key] || key,
    isCustom: false,
  }));

  const menus = customMenus.value || [];
  const customMenuItems = menus.map(menu => ({
    key: `custom_menu_${menu.id}`,
    label: menu.name,
    icon: menu.icon,
    isCustom: true,
  }));

  return [...staticMenus, ...customMenuItems];
});

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Fetch custom menus if not already loaded
  if (customMenus.value.length === 0) {
    store.dispatch('customMenu/getCustomMenus');
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    // Convert selectedMenuPermissions array to object { menuKey: true/false }
    // Always send explicit values for all menus to distinguish "none selected" from "not configured"
    const menuPermissionsObject = {};
    allMenuPermissions.value.forEach(menuItem => {
      menuPermissionsObject[menuItem.key] =
        selectedMenuPermissions.value.includes(menuItem.key);
    });

    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
      menu_permissions: menuPermissionsObject,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="permission in AVAILABLE_CUSTOM_ROLE_PERMISSIONS"
            :key="permission"
            class="flex items-center"
          >
            <input
              :id="permission"
              v-model="selectedPermissions"
              type="checkbox"
              :value="permission"
              name="permissions"
              class="ltr:mr-2 rtl:ml-2"
            />
            <label :for="permission" class="text-sm font-normal">
              {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
            </label>
          </div>
        </div>
      </div>

      <div class="w-full">
        <label class="text-sm font-medium">
          {{ $t('CUSTOM_ROLE.FORM.MENU_PERMISSIONS.LABEL') }}
        </label>
        <p class="text-xs text-n-muted mb-2">
          {{ $t('CUSTOM_ROLE.FORM.MENU_PERMISSIONS.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2.5 mb-4 mt-2">
          <div
            v-for="menuItem in allMenuPermissions"
            :key="menuItem.key"
            class="flex items-center"
          >
            <input
              :id="`menu-${menuItem.key}`"
              v-model="selectedMenuPermissions"
              type="checkbox"
              :value="menuItem.key"
              name="menu_permissions"
              class="ltr:mr-2 rtl:ml-2"
            />
            <label
              :for="`menu-${menuItem.key}`"
              class="text-sm font-normal flex items-center gap-1"
            >
              <span
                v-if="menuItem.icon"
                :class="menuItem.icon"
                class="size-4"
              />
              {{ menuItem.label }}
              <span
                v-if="menuItem.isCustom"
                class="text-xs text-n-slate-10 bg-n-alpha-1 px-1.5 py-0.5 rounded"
              >
                {{ $t('CUSTOM_ROLE.FORM.MENU_PERMISSIONS.CUSTOM') }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
